import { useState } from "react";
import { BadgeCheck, Languages, Loader2, Mail, MapPin, Pencil, Phone, Stethoscope, UserRound } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "../lib/supabaseClient";
import { PhotoUpload } from "./PhotoUpload";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import {
  SpecialtyMultiSelect,
  SpecialtyChips,
  LanguageMultiSelect,
  LanguageChips,
} from "./SpecialtyMultiSelect";
import { SuburbAutocomplete } from "./SuburbAutocomplete";
import { NameCorrectionRequest } from "./NameCorrectionRequest";
import { AppointmentTypeSelect } from "./AppointmentTypeSelect";
import { normaliseExpertiseList } from "../lib/clinicalOptions";

const removeCsvItem = (csv, item) =>
  (csv || "").split(",").map((value) => value.trim()).filter((value) => value && value !== item).join(", ");

export const EditableClinicianProfile = ({ clinician, onUpdated }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});

  const beginEditing = () => {
    setForm({
      photoUrl: clinician.photo_url || "",
      specialty: clinician.specialty || "",
      clinical_expertise: clinician.clinical_expertise || [],
      appointment_types: clinician.appointment_types || [],
      wait_time: clinician.wait_time || "",
      languages: clinician.languages || "",
      phone: clinician.phone || "",
      gender: clinician.gender || "",
      practice_address: clinician.practice_address || "",
      suburb: clinician.suburb || "",
      postcode: clinician.postcode || "",
      state: clinician.state || "VIC",
    });
    setEditing(true);
  };

  const saveProfile = async () => {
    if (!form.specialty.trim()) {
      toast.error("Please select at least one specialty.");
      return;
    }
    if (!form.suburb.trim()) {
      toast.error("Please enter your practising suburb.");
      return;
    }
    setSaving(true);
    const update = {
      photo_url: form.photoUrl || null,
      specialty: form.specialty.trim(),
      clinical_expertise: normaliseExpertiseList(form.specialty.split(",")),
      appointment_types: form.appointment_types,
      wait_time: form.wait_time.trim() || null,
      languages: form.languages.trim() || null,
      phone: form.phone.trim() || null,
      gender: form.gender || null,
      practice_address: form.practice_address.trim() || null,
      suburb: form.suburb.trim(),
      postcode: form.postcode.trim() || null,
      state: form.state || "VIC",
    };
    const { data, error } = await supabase
      .from("clinicians")
      .update(update)
      .eq("id", clinician.id)
      .select()
      .maybeSingle();
    setSaving(false);
    if (error) {
      toast.error("Could not update your profile: " + error.message);
      return;
    }
    onUpdated(data);
    setEditing(false);
    toast.success("Profile updated.");
  };

  if (editing) {
    return (
      <section className="rounded-md border border-slate-200 bg-white p-6" data-testid="clinician-profile-edit-section">
        <div className="mb-5 flex items-start justify-between gap-4">
          <div>
            <h2 className="font-heading text-xl font-semibold text-slate-900">Edit directory profile</h2>
            <p className="mt-1 text-sm text-slate-500">Your name, profession, email and AHPRA registration number remain locked.</p>
          </div>
        </div>
        <div className="mb-5 space-y-2">
          <Label>Profile photo</Label>
          <PhotoUpload value={form.photoUrl} onChange={(url) => setForm((current) => ({ ...current, photoUrl: url }))} testId="profile-edit-photo" />
        </div>
        <div className="grid gap-5 sm:grid-cols-2">
          <div className="space-y-2 sm:col-span-2">
            <Label>Clinical expertise</Label>
            <SpecialtyMultiSelect value={form.specialty} profession={clinician.profession} onChange={(csv) => setForm((current) => ({ ...current, specialty: csv }))} testId="profile-edit-specialty" />
            <SpecialtyChips value={form.specialty} onRemove={(item) => setForm((current) => ({ ...current, specialty: removeCsvItem(current.specialty, item) }))} />
          </div>
          <div className="space-y-2">
            <Label>Appointment type</Label>
            <AppointmentTypeSelect value={form.appointment_types} onChange={(value) => setForm((current) => ({ ...current, appointment_types: value }))} testId="profile-edit-appointment-types" />
            <p className="text-xs text-slate-500">Choose either or both. Leave blank if you do not want this shown as a search filter.</p>
          </div>
          <div className="space-y-2">
            <Label>Typical wait time</Label>
            <Input value={form.wait_time} onChange={(event) => setForm((current) => ({ ...current, wait_time: event.target.value }))} placeholder="Available now, 1–2 weeks…" />
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Languages</Label>
            <LanguageMultiSelect value={form.languages} onChange={(csv) => setForm((current) => ({ ...current, languages: csv }))} testId="profile-edit-languages" />
            <LanguageChips value={form.languages} onRemove={(item) => setForm((current) => ({ ...current, languages: removeCsvItem(current.languages, item) }))} />
          </div>
          <div className="space-y-2">
            <Label>Phone</Label>
            <Input value={form.phone} onChange={(event) => setForm((current) => ({ ...current, phone: event.target.value }))} placeholder="Contact phone number" data-testid="profile-edit-phone" />
          </div>
          <div className="space-y-2">
            <Label>Gender</Label>
            <Select value={form.gender} onValueChange={(value) => setForm((current) => ({ ...current, gender: value }))}>
              <SelectTrigger data-testid="profile-edit-gender"><SelectValue placeholder="Select" /></SelectTrigger>
              <SelectContent className="bg-white">
                <SelectItem value="Female">Female</SelectItem>
                <SelectItem value="Male">Male</SelectItem>
                <SelectItem value="Non-binary">Non-binary</SelectItem>
                <SelectItem value="Prefer not to say">Prefer not to say</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-2 sm:col-span-2">
            <Label>Practising address <span className="font-normal text-slate-400">(optional)</span></Label>
            <Input value={form.practice_address} onChange={(event) => setForm((current) => ({ ...current, practice_address: event.target.value }))} placeholder="Street address or clinic name" data-testid="profile-edit-practice-address" />
          </div>
          <div className="space-y-2">
            <Label>Suburb</Label>
            <SuburbAutocomplete required value={form.suburb} onChange={(value) => setForm((current) => ({ ...current, suburb: value }))} onSelect={(option) => setForm((current) => ({ ...current, suburb: option.suburb, postcode: option.postcode, state: option.state }))} testId="profile-edit-suburb" />
          </div>
          <div className="space-y-2">
            <Label>Postcode</Label>
            <Input value={form.postcode} onChange={(event) => setForm((current) => ({ ...current, postcode: event.target.value }))} placeholder="Postcode" data-testid="profile-edit-postcode" />
          </div>
        </div>
        <div className="mt-6 flex justify-end gap-3">
          <Button type="button" variant="outline" onClick={() => setEditing(false)} disabled={saving}>Cancel</Button>
          <Button type="button" onClick={saveProfile} disabled={saving} data-testid="profile-edit-save">
            {saving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />} Save changes
          </Button>
        </div>
      </section>
    );
  }

  const location = [clinician.practice_address, clinician.suburb, clinician.state, clinician.postcode].filter(Boolean).join(", ");

  return (
    <section data-testid="clinician-profile-section">
      <div className="rounded-md border border-slate-200 bg-white p-6">
        <div className="flex items-start gap-4">
          <div className="flex h-16 w-16 shrink-0 items-center justify-center overflow-hidden rounded-md bg-secondary font-heading text-xl font-semibold text-secondary-foreground">
            {clinician.photo_url ? (
              <img src={clinician.photo_url} alt="Profile" className="h-full w-full object-cover" />
            ) : (
              ((clinician.first_name || "").charAt(0) + (clinician.last_name || "").charAt(0)).toUpperCase()
            )}
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="flex items-center gap-2">
                  <h1 className="font-heading text-2xl font-semibold tracking-tight text-slate-900" data-testid="clinician-profile-name">
                    {clinician.first_name} {clinician.last_name}
                  </h1>
                  {clinician.is_verified && <BadgeCheck className="h-5 w-5 text-primary" data-testid="clinician-profile-verified" />}
                </div>
                <p className="text-sm text-slate-600">{clinician.profession}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                <NameCorrectionRequest clinician={clinician} onUpdated={onUpdated} />
                <Button type="button" variant="outline" size="sm" onClick={beginEditing} data-testid="clinician-profile-edit-button">
                  <Pencil className="mr-2 h-4 w-4" /> Edit profile
                </Button>
              </div>
            </div>
            <div className="mt-4 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
              <span className="inline-flex items-start gap-1.5"><Stethoscope className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{clinician.specialty}</span>
              <span className="inline-flex items-start gap-1.5"><MapPin className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{location}</span>
              {clinician.languages && <span className="inline-flex items-start gap-1.5"><Languages className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{clinician.languages}</span>}
              {clinician.phone && <span className="inline-flex items-start gap-1.5"><Phone className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{clinician.phone}</span>}
              <span className="inline-flex items-start gap-1.5"><Mail className="mt-0.5 h-4 w-4 shrink-0 text-slate-400" />{clinician.email}</span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
